// =====================================================================
// PDF EXPORT — "تصدير PDF" button on every saved document
// =====================================================================
// Adds a custom button to ANY saved form. Opens a tiny dialog asking
// for the Print Format (defaulted to the doctype's default), then
// downloads via Frappe's official PDF endpoint:
//   /api/method/frappe.utils.print_format.download_pdf
// The file is saved with the document's name as filename — natively
// handled by Frappe (Content-Disposition header).
//
// SAFE: zero global prototype patches. We listen for SPA route changes
// (frappe.router.on 'change') and only call the OFFICIAL Frappe API
// `cur_frm.add_custom_button(...)`. No DOM observers, no monkey patches.
// =====================================================================

import { uxPrefs } from "./ux_prefs.js";

function L(ar, en) {
	return document.documentElement.getAttribute("lang") === "ar" ? ar : en;
}

function enabled() {
	try { return !!uxPrefs.get().pdf_export; } catch (e) { return true; }
}

// Smart retry helper — same flow as the dialog action but called from
// the error handler when we auto-fall-back to format="Standard".
function retryDownload(frm, v) {
	const params = new URLSearchParams({
		doctype: frm.doctype,
		name: frm.docname,
		format: v.print_format || "Standard",
		no_letterhead: v.letter_head ? "0" : "1",
		language: v.language || "en",
		_lang: v.language || "en",
		pdf_generator: "chrome",      // same Chrome backend on retry
	});
	if (v.letter_head) params.set("letterhead", v.letter_head);
	if (v.orientation) params.set("orientation", v.orientation);
	const url = "/api/method/frappe.utils.print_format.download_pdf?" + params.toString();
	const xhr = new XMLHttpRequest();
	xhr.open("GET", url, true);
	xhr.responseType = "blob";
	try { if (window.frappe && frappe.csrf_token) xhr.setRequestHeader("X-Frappe-CSRF-Token", frappe.csrf_token); } catch (e) {}
	xhr.withCredentials = true;
	xhr.onload = function () {
		if (xhr.status === 200 && xhr.response && (xhr.response.type === "application/pdf" || xhr.response.size > 1024)) {
			const objUrl = URL.createObjectURL(new Blob([xhr.response], { type: "application/pdf" }));
			const a = document.createElement("a");
			a.href = objUrl;
			a.download = (frm.docname || "document") + ".pdf";
			a.style.display = "none";
			document.body.appendChild(a);
			a.click();
			setTimeout(() => { try { a.remove(); URL.revokeObjectURL(objUrl); } catch (e) {} }, 4000);
			try {
				frappe.show_alert({
					message: L("تم التنزيل ✓ ", "Downloaded ✓ ") + frm.docname + ".pdf",
					indicator: "green",
				}, 4);
			} catch (e) {}
		} else {
			// Standard ALSO failed — last resort: open printview
			handleXhrError(xhr.response, frm, { ...v, print_format: "Standard" });
		}
	};
	xhr.onerror = function () { handleXhrError(null, frm, { ...v, print_format: "Standard" }); };
	xhr.send();
}

// Smart fallback when a Frappe print-format has a Jinja bug:
//   1. parse the response → show the real error
//   2. AUTO-RETRY with format="Standard" (built-in safe format)
//   3. if STILL failing → open printview so user can pick a different
//      format manually
function handleXhrError(payload, frm, v) {
	const usedFormat = (v && v.print_format) || "";
	const finish = (errMsg) => {
		// If we weren't already on Standard, auto-retry with Standard
		if (usedFormat && usedFormat !== "Standard") {
			try {
				frappe.show_alert({
					message: L(
						"النموذج «" + usedFormat + "» به خطأ — جارٍ التحويل تلقائياً إلى «Standard»",
						'Format "' + usedFormat + '" has a bug — auto-retrying with "Standard"'
					),
					indicator: "orange",
				}, 6);
			} catch (e) {}
			retryDownload(frm, { ...(v || {}), print_format: "Standard" });
			return;
		}
		// Already tried Standard or no format → open print view
		try {
			frappe.show_alert({
				message: errMsg || L(
					"تعذّر التنزيل المباشر — جارٍ فتح عرض الطباعة (اختر «حفظ كـ PDF»)",
					"Direct download failed — opening print view (choose 'Save as PDF')"
				),
				indicator: "red",
			}, 9);
		} catch (e) {}
		const params = new URLSearchParams({
			doctype: frm.doctype,
			name: frm.docname,
			format: "Standard",
			trigger_print: "1",
			no_letterhead: (v && v.letter_head) ? "0" : "1",
			_lang: (v && v.language) || "en",
		});
		if (v && v.letter_head) params.set("letterhead", v.letter_head);
		setTimeout(() => window.open("/printview?" + params.toString(), "_blank"), 800);
	};
	let msg = "";
	if (!payload) { finish(); return; }
	if (payload.text) {
		payload.text().then((txt) => {
			try {
				const j = JSON.parse(txt);
				if (j && j._server_messages) {
					const list = JSON.parse(j._server_messages);
					if (list && list.length) {
						msg = (typeof list[0] === "string" ? JSON.parse(list[0]) : list[0]).message || "";
					}
				}
				if (!msg && j && j.exception) msg = String(j.exception).slice(0, 200);
			} catch (e) {
				const m = txt.match(/<h\d[^>]*>([^<]+)<\/h\d>/i);
				if (m) msg = m[1];
			}
			finish(msg ? L("خطأ: ", "Error: ") + msg : "");
		}).catch(() => finish());
	} else {
		finish();
	}
}

// remember which form instances we've already decorated
const installed = new WeakSet();

function shouldAddButton(frm) {
	if (!enabled()) return false;
	if (!frm || !frm.doc || !frm.docname) return false;
	try { if (frm.is_new && frm.is_new()) return false; } catch (e) {}
	if (frm.doc.docstatus === undefined) return false;
	return true;
}

function tryAddButton() {
	try {
		const frm = window.cur_frm;
		if (!shouldAddButton(frm)) return;
		if (installed.has(frm)) {
			// page may have re-rendered — re-add if missing
			if (frm.page && frm.page.wrapper &&
				frm.page.wrapper.find(".e16-pdf-export-btn").length === 0) {
				installed.delete(frm);
			} else {
				return;
			}
		}
		const $btn = frm.add_custom_button(
			L("📄 تصدير PDF", "📄 Export PDF"),
			() => openDialog(frm),
			null
		);
		if ($btn && $btn.addClass) {
			$btn.addClass("e16-pdf-export-btn");
			// move it to standard-actions if possible for visibility
		}
		installed.add(frm);
	} catch (e) {
		console.warn("[e16] pdf-export add", e);
	}
}

function openDialog(frm) {
	try {
		// "Standard" is Frappe's most reliable built-in print format.
		// The doctype's auto-default (e.g. "Journal Auditing Voucher")
		// often has Jinja bugs in stock ERPNext → we prefer Standard.
		const defaultFmt = "Standard";
		const defaultLh =
			frappe.defaults && frappe.defaults.get_default("letter_head");
		const lang = document.documentElement.getAttribute("lang") || "en";

		const d = new frappe.ui.Dialog({
			title: L("تصدير المستند ك PDF", "Export as PDF"),
			fields: [
				{
					fieldname: "mode",
					fieldtype: "Select",
					label: L("الطريقة", "Method"),
					options: [
						L("نموذج طباعة Frappe (مع ترويسة وتنسيق)", "Frappe Print Format (with letterhead & formatting)"),
						L("طباعة العرض الحالي مباشرةً (يعمل دائماً)", "Print Current View directly (always works)")
					].join("\n"),
					default: L("نموذج طباعة Frappe (مع ترويسة وتنسيق)", "Frappe Print Format (with letterhead & formatting)"),
				},
				{
					fieldname: "print_format",
					fieldtype: "Link",
					label: L("نموذج الطباعة", "Print Format"),
					options: "Print Format",
					default: defaultFmt,
					get_query: () => ({ filters: { doc_type: frm.doctype } }),
					depends_on: 'eval:doc.mode && doc.mode.indexOf("Frappe")>-1 || doc.mode && doc.mode.indexOf("Frappe")===-1 && doc.mode.indexOf("العرض")===-1',
					description: L("الافتراضي «Standard» — آمن لكل المستندات", "Default 'Standard' is safe for all doctypes"),
				},
				{
					fieldname: "letter_head",
					fieldtype: "Link",
					label: L("ترويسة الرسائل", "Letter Head"),
					options: "Letter Head",
					default: defaultLh || "",
				},
				{
					fieldname: "language",
					fieldtype: "Select",
					label: L("اللغة", "Language"),
					options: ["", "en", "ar"].join("\n"),
					default: lang === "ar" ? "ar" : "en",
				},
				{
					fieldname: "orientation",
					fieldtype: "Select",
					label: L("اتجاه الصفحة", "Orientation"),
					options: ["Portrait", "Landscape"].join("\n"),
					default: "Portrait",
				},
			],
			primary_action_label: L("تحميل PDF", "Download PDF"),
			primary_action: (v) => {
				const useCurrentView = v.mode && v.mode.indexOf("العرض") > -1;
				if (useCurrentView) {
					d.hide();
					setTimeout(() => window.print(), 100);
					return;
				}
				// DIRECT DOWNLOAD via XMLHttpRequest+blob — handles errors
				// (Frappe sometimes returns HTML 500 instead of PDF when a
				// print format has Jinja bugs); we read the response type
				// and surface a clear toast on failure instead of saving a
				// broken file. "Standard" format is the safe default.
				const params = new URLSearchParams({
					doctype: frm.doctype,
					name: frm.docname,
					format: v.print_format || defaultFmt,
					no_letterhead: v.letter_head ? "0" : "1",
					language: v.language || "en",
					_lang: v.language || "en",
					// 🎯 Use Frappe v16's NATIVE Chrome headless PDF backend.
					// Bypasses wkhtmltopdf entirely (not installed on this
					// machine). Frappe auto-downloads Chromium if missing.
					pdf_generator: "chrome",
				});
				if (v.letter_head) params.set("letterhead", v.letter_head);
				if (v.orientation) params.set("orientation", v.orientation);
				const url =
					"/api/method/frappe.utils.print_format.download_pdf?" +
					params.toString();

				d.hide();
				let waiter;
				try {
					waiter = frappe.show_alert({
						message: L("جارٍ تحضير PDF…", "Preparing PDF…"),
						indicator: "blue",
					}, 30);
				} catch (e) {}

				const xhr = new XMLHttpRequest();
				xhr.open("GET", url, true);
				xhr.responseType = "blob";
				try {
					if (window.frappe && frappe.csrf_token) {
						xhr.setRequestHeader("X-Frappe-CSRF-Token", frappe.csrf_token);
					}
				} catch (e) {}
				xhr.withCredentials = true;
				xhr.onload = function () {
					try { waiter && waiter.remove && waiter.remove(); } catch (e) {}
					if (xhr.status === 200 && xhr.response) {
						const blob = xhr.response;
						const okPdf = /pdf/i.test(blob.type || "") || blob.size > 1024;
						if (!okPdf) {
							handleXhrError(blob, frm, v);
							return;
						}
						const objUrl = URL.createObjectURL(
							new Blob([blob], { type: "application/pdf" })
						);
						const a = document.createElement("a");
						a.href = objUrl;
						a.download = (frm.docname || "document") + ".pdf";
						a.style.display = "none";
						document.body.appendChild(a);
						a.click();
						setTimeout(() => {
							try { a.remove(); } catch (e) {}
							try { URL.revokeObjectURL(objUrl); } catch (e) {}
						}, 4000);
						try {
							frappe.show_alert({
								message: L("تم التنزيل ✓ ", "Downloaded ✓ ") + frm.docname + ".pdf",
								indicator: "green",
							}, 4);
						} catch (e) {}
					} else {
						handleXhrError(xhr.response, frm, v);
					}
				};
				xhr.onerror = function () {
					try { waiter && waiter.remove && waiter.remove(); } catch (e) {}
					handleXhrError(null, frm, v);
				};
				xhr.send();
			},
		});
		d.show();
	} catch (e) {
		console.warn("[e16] pdf-export dialog", e);
		try { frappe.msgprint(L("تعذّر فتح خيارات التصدير", "Could not open export dialog")); } catch (e2) {}
	}
}

export const pdfExport = {
	mount() {
		const start = () => {
			try {
				if (window.frappe && frappe.router && frappe.router.on) {
					frappe.router.on("change", () => {
						// give Frappe time to (re)build cur_frm
						setTimeout(tryAddButton, 300);
						setTimeout(tryAddButton, 900);
						setTimeout(tryAddButton, 1800);
					});
				}
				// initial attempts in case we landed directly on a form
				setTimeout(tryAddButton, 600);
				setTimeout(tryAddButton, 1500);
			} catch (e) {
				console.warn("[e16] pdf-export mount", e);
			}
		};

		if (window.frappe && frappe.router) {
			start();
		} else {
			let n = 0;
			const iv = setInterval(() => {
				n++;
				if (window.frappe && frappe.router) {
					start();
					clearInterval(iv);
				} else if (n > 80) {
					clearInterval(iv);
				}
			}, 250);
		}
	},
};
